/*
  TotalVet homepage: newsletter form (sections/tv-home-signup.liquid).
  Port of initSignupForm() in the WP theme's assets/js/custom/homepage_v3.js: trims the email, shows
  "Please enter a valid email." / "The email is not valid." below the form instead of submitting, and
  switches the button to "Sending…" while it posts.
  Klaviyo mode (data-klaviyo-list): subscribes the email to the Klaviyo list through the Client Subscriptions
  API, like the WP form did, and shows the success/error message inline. Shopify mode: lets the native customer
  form post; success and server errors are rendered by Liquid after the reload.
*/
(() => {
  if (customElements.get('tv-home-signup-form')) return;

  const EMAIL_PATTERN = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

  class TvHomeSignupForm extends HTMLElement {
    connectedCallback() {
      this.form = this.querySelector('form');
      this.input = this.querySelector('[data-email]');
      this.button = this.querySelector('[data-submit]');
      this.message = this.querySelector('[data-message]');
      if (!this.form || !this.input || !this.button) return;

      this.buttonLabel = this.button.textContent;

      // Shopify reports the posted state to every customer form on the page; keep the result on the
      // form that was actually submitted (the redirect carries its id as the URL hash).
      const hash = window.location.hash;
      if (this.message && !this.message.hidden && hash && this.form.id && hash !== `#${this.form.id}`) {
        this.clearMessage();
      }

      this.onSubmit = (event) => {
        const email = this.input.value.trim();
        this.clearMessage();

        if (!email) {
          event.preventDefault();
          this.showError(this.dataset.errorEmpty);
          this.input.focus();
          return;
        }
        if (!EMAIL_PATTERN.test(email)) {
          event.preventDefault();
          this.showError(this.dataset.errorInvalid);
          this.input.focus();
          return;
        }

        this.input.value = email;
        this.button.textContent = this.dataset.sendingLabel || this.buttonLabel;

        if (this.dataset.klaviyoList) {
          event.preventDefault();
          this.button.disabled = true;
          this.subscribeToKlaviyo(email);
          return;
        }

        setTimeout(() => {
          this.button.disabled = true;
        }, 0);
      };

      this.onPageShow = (event) => {
        if (!event.persisted) return;
        this.button.disabled = false;
        this.button.textContent = this.buttonLabel;
      };

      this.form.addEventListener('submit', this.onSubmit);
      window.addEventListener('pageshow', this.onPageShow);
    }

    disconnectedCallback() {
      if (!this.onSubmit) return;
      this.form.removeEventListener('submit', this.onSubmit);
      window.removeEventListener('pageshow', this.onPageShow);
    }

    // WP: POST manage.kmail-lists.com/ajax/subscriptions/subscribe { g: LIST_ID, email, $consent: ['email'], source: 'custom_form' }.
    // Same thing on the current Klaviyo Client API: 202 with an empty body on success.
    async subscribeToKlaviyo(email) {
      const company = this.dataset.klaviyoCompany;
      const list = this.dataset.klaviyoList;
      let ok = false;
      let errorText = this.dataset.errorGeneric || '';
      try {
        const response = await fetch(`https://a.klaviyo.com/client/subscriptions/?company_id=${encodeURIComponent(company)}`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/vnd.api+json', revision: '2025-07-15' },
          body: JSON.stringify({
            data: {
              type: 'subscription',
              attributes: {
                custom_source: 'custom_form',
                profile: {
                  data: {
                    type: 'profile',
                    attributes: {
                      email,
                      subscriptions: { email: { marketing: { consent: 'SUBSCRIBED' } } },
                    },
                  },
                },
              },
              relationships: { list: { data: { type: 'list', id: list } } },
            },
          }),
        });
        ok = response.ok;
        if (!ok) {
          try {
            const body = await response.json();
            const detail = body && body.errors && body.errors[0] && body.errors[0].detail;
            if (detail) errorText = detail;
          } catch (e) {
            /* keep the generic message */
          }
        }
      } catch (e) {
        /* network error: keep the generic message */
      }

      this.button.disabled = false;
      this.button.textContent = this.buttonLabel;
      if (ok) {
        this.input.value = '';
        this.showSuccess(this.dataset.successMessage);
      } else {
        this.showError(errorText);
      }
    }

    showSuccess(text) {
      if (!this.message) return;
      this.message.textContent = text || '';
      this.message.classList.remove('tv-home-signup__message--error');
      this.message.classList.add('tv-home-signup__message--success');
      this.message.hidden = false;
    }

    clearMessage() {
      if (!this.message) return;
      this.message.textContent = '';
      this.message.classList.remove('tv-home-signup__message--error', 'tv-home-signup__message--success');
      this.message.hidden = true;
    }

    showError(text) {
      if (!this.message) return;
      this.message.textContent = text || '';
      this.message.classList.add('tv-home-signup__message--error');
      this.message.hidden = false;
    }
  }

  customElements.define('tv-home-signup-form', TvHomeSignupForm);
})();
